/**
 * 订单表单弹窗「关联客户」区块：客户搜索下拉选择与已选客户信息展示
 */
import type { Dispatch, SetStateAction } from 'react';
import { Controller, type Control, type FieldErrors, type UseFormSetValue } from 'react-hook-form';
import type { OrderFormData } from '../../OrderFormSchema';
import type { PartyOption } from '../types';
import { SectionHeader } from '../SectionHeader';
import { inp } from '../utils';

interface CustomerSectionProps {
  control: Control<OrderFormData>;
  errors: FieldErrors<OrderFormData>;
  form: OrderFormData;
  setValue: UseFormSetValue<OrderFormData>;
  selectedCustomer: PartyOption | null;
  customerSearch: string;
  showCustomerDrop: boolean;
  filteredCustomers: PartyOption[];
  setCustomerSearch: Dispatch<SetStateAction<string>>;
  setShowCustomerDrop: Dispatch<SetStateAction<boolean>>;
}

export function CustomerSection({ control, errors, form, setValue, selectedCustomer, customerSearch, showCustomerDrop, filteredCustomers, setCustomerSearch, setShowCustomerDrop }: CustomerSectionProps) {
  return (
    /* ③ 关联客户 — 独立区块，位于时间安排下方 */
    <div>
      <SectionHeader icon="ri-user-star-line" title="关联客户" color="bg-violet-50 text-violet-500" />
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
          <i className="ri-search-line text-gray-400 text-sm" />
        </div>
        <Controller
          name="customerId"
          control={control}
          render={() => (
            <input
              type="text"
              value={showCustomerDrop ? customerSearch : (selectedCustomer ? `${selectedCustomer.fullName ?? ''}${selectedCustomer.companyName ? ` · ${selectedCustomer.companyName}` : ''}` : '')}
              onFocus={() => { setShowCustomerDrop(true); setCustomerSearch(''); }}
              onChange={e => {
                if (showCustomerDrop) {
                  setCustomerSearch(e.target.value);
                }
              }}
              placeholder="搜索客户姓名 / 公司 / 电话 *"
              className={`${inp(errors.customerId?.message)} pl-9`}
            />
          )}
        />
        {form.customerId && !showCustomerDrop && (
          <button onClick={() => {
            setValue('customerId', '');
            setCustomerSearch('');
          }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-500 cursor-pointer">
            <i className="ri-close-circle-fill text-base" />
          </button>
        )}
        {showCustomerDrop && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowCustomerDrop(false)} />
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl z-20 max-h-52 overflow-y-auto shadow-sm">
              {filteredCustomers.length === 0
                ? <p className="px-4 py-3 text-sm text-gray-400 text-center">未找到匹配客户</p>
                : filteredCustomers.slice(0, 30).map(c => (
                  <button key={c.id} onClick={() => {
                    setValue('customerId', c.id);
                    setShowCustomerDrop(false);
                    setCustomerSearch('');
                  }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-teal-50 text-left cursor-pointer transition-colors">
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center shrink-0 overflow-hidden">
                      {c.avatar
                        ? <img src={c.avatar} alt="" className="w-full h-full object-cover object-top" />
                        : <span className="text-teal-600 text-xs font-semibold">{(c.fullName ?? '?').charAt(0)}</span>
                      }
                    </div>
                    <div>
                      <p className="text-sm text-gray-800 font-medium">{c.fullName}</p>
                      {c.companyName && <p className="text-xs text-gray-400">{c.companyName}</p>}
                    </div>
                    {c.phone && <p className="ml-auto text-xs text-gray-400 shrink-0">{c.phone}</p>}
                  </button>
                ))
              }
            </div>
          </>
        )}
      </div>
      {errors.customerId && <p className="text-xs text-rose-500 mt-1">{errors.customerId.message}</p>}
      {/* 已选客户简要信息 */}
      {selectedCustomer && !showCustomerDrop && (
        <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-violet-50 rounded-lg">
          <div className="w-6 h-6 rounded-full bg-violet-200 flex items-center justify-center shrink-0 overflow-hidden">
            {selectedCustomer.avatar
              ? <img src={selectedCustomer.avatar} alt="" className="w-full h-full object-cover object-top" />
              : <span className="text-violet-600 text-xs font-semibold">{(selectedCustomer.fullName ?? '?').charAt(0)}</span>
            }
          </div>
          <span className="text-sm text-violet-700 font-medium">{selectedCustomer.fullName}</span>
          {selectedCustomer.companyName && <span className="text-xs text-violet-400">{selectedCustomer.companyName}</span>}
          {selectedCustomer.phone && <span className="ml-auto text-xs text-gray-400">{selectedCustomer.phone}</span>}
        </div>
      )}
    </div>
  );
}